import {startOfDay} from 'date-fns'

import {parseApiDate, toApiDateString} from '../jobs/job-schedule-format'
import {createUuid} from '../lib/create-uuid'
import type {
    BuildOutRequirement,
    BuildOutScheduling,
    ServiceTemplateRequirement,
} from './build-out-types'
import type {ScheduleJob, ServiceSchedule} from './schemas'
import {pythonWeekdaysToJs} from './weekday-conversion'

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

export function formatCadenceWeekdays(weekdays: number[]): string {
    return [...weekdays]
        .sort((left, right) => left - right)
        .map((day) => WEEKDAY_LABELS[day] ?? String(day))
        .join(', ')
}

export function createEmptyScheduling(): BuildOutScheduling {
    const today = startOfDay(new Date())

    return {
        pattern: 'deploy_service_pickup',
        startDate: today,
        endDate: today,
        startTime: null,
        endTime: null,
        cadence: {weekdays: [], everyNWeeks: 1},
        serviceTemplate: [],
        serviceTypeId: null,
        serviceNotes: '',
        deployNotes: '',
        pickupNotes: '',
        skippedServiceDates: [],
        markJobsReadyForDispatch: true,
    }
}

/** Match a generated service visit to its persisted schedule job, if one exists. */
export function findPersistedServiceJob(
    schedule: ServiceSchedule,
    serviceDate: Date,
): ScheduleJob | undefined {
    const dateKey = toApiDateString(serviceDate)
    return schedule.jobs.find((job) => job.scheduleRole === 'service' && job.startDate === dateKey)
}

export function schedulingFromServiceSchedule(schedule: ServiceSchedule): BuildOutScheduling {
    const serviceTemplate: ServiceTemplateRequirement[] = [
        ...schedule.workerRequirements.map((requirement) => ({
            id: createUuid(),
            kind: 'worker' as const,
            typeId: requirement.typeId,
            qty: requirement.quantity,
        })),
        ...schedule.assetRequirements.map((requirement) => ({
            id: createUuid(),
            kind: 'equipment' as const,
            typeId: requirement.typeId,
            qty: requirement.quantity,
        })),
    ]

    const deployJob = schedule.jobs.find((job) => job.scheduleRole === 'deploy')
    const pickupJob = schedule.jobs.find((job) => job.scheduleRole === 'pickup')

    return {
        pattern: 'deploy_service_pickup',
        startDate: parseApiDate(schedule.startDate),
        endDate: parseApiDate(schedule.endDate),
        cadence: {
            weekdays: pythonWeekdaysToJs(schedule.cadenceWeekdays),
            everyNWeeks: 1,
        },
        serviceTemplate,
        serviceTypeId: schedule.serviceTypeId,
        startTime: schedule.serviceStartTime,
        endTime: schedule.serviceEndTime,
        serviceNotes: schedule.notes ?? '',
        deployNotes: deployJob?.notes ?? '',
        pickupNotes: pickupJob?.notes ?? '',
        skippedServiceDates: schedule.jobs
            .filter((job) => job.scheduleRole === 'service' && job.status === 'cancelled')
            .map((job) => job.startDate),
        markJobsReadyForDispatch: true,
        ...(schedule.visitsOnly ? {visitsOnly: true} : {}),
    }
}

/** Derive deploy/pickup stage requirements from a persisted schedule for draft job generation. */
export function lineRequirementsFromSchedule(schedule: ServiceSchedule): BuildOutRequirement[] {
    const deployJob = schedule.jobs.find((job) => job.scheduleRole === 'deploy')
    if (deployJob == null) {
        return []
    }

    return [
        ...deployJob.workerRequirements.map((requirement) => ({
            kind: 'worker' as const,
            typeId: requirement.typeId,
            qty: requirement.quantity,
        })),
        ...deployJob.assetRequirements.map((requirement) => ({
            kind: 'equipment' as const,
            typeId: requirement.typeId,
            qty: requirement.quantity,
        })),
    ]
}
